import React, { useState } from 'react';
import {
  Card, CardMedia, CardContent, Typography, Box
} from '@mui/material';
import { motion } from 'framer-motion';

/*
 * ClassCard
 * ------------
 * Card used to show a class (name, photo, description).
 *
 * Props:
 * - classItem: object with fields { name, description, photo }
 *    - name: string title of the class, used for heading and image alt text.
 *    - description: long text describing the class (may be truncated in UI).
 *    - photo: path to image (string)
 * - delay: number (seconds) used by framer-motion to stagger entry animation.
 * - isFirst: boolean (optional) - if true, the image will be eager-loaded with high priority for LCP optimization
 *
 * Contract / behavior:
 * - The component trims long descriptions to 'charLimit' and exposes a
 *   "Read more" / "Show less" toggle for full text.
 * - Images are rendered with an 'alt' attribute using the
 *   class name.
 * - Entry/hover animations are provided by framer-motion
 *
 * Edge cases:
 * - If 'classItem.description' is empty or shorter than 'charLimit', the
 *   toggle is not rendered.
 * - The component does not perform image fetching logic or error handling for
 *   missing photos; the parent provides a valid 'photo' value.
 */

  import config from '../config';
  const url = config.API_URL;

function ClassCard({ classItem, delay, isFirst = false }) {
  const [expanded, setExpanded] = useState(false);
  const charLimit = 200;
  const isLong = classItem.description.length > charLimit;
  const preview = classItem.description.slice(0, charLimit);


  let photoUrl = classItem.photo
    ? (typeof classItem.photo === 'string' && classItem.photo.startsWith('http')
        ? classItem.photo
        : url + classItem.photo)
    : '';
  
  // Optimize class photos from Cloudinary
  if (photoUrl.includes('cloudinary.com')) {
    photoUrl = photoUrl.replace('/upload/', '/upload/f_auto,q_auto,w_400,c_limit/');
  }
  
  const altText = classItem.alt_text || classItem.name;

  return (
    // framer-motion wrapper for entrance animation
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.4, delay }}
      viewport={{ once: true, margin: "-50px" }}
      style={{ height: '100%', willChange: 'opacity, transform' }}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#222',
          color: '#fff',
          borderRadius: 2,
          boxShadow: 4,
          transition: 'transform 0.3s ease-in-out',
          p: { xs: 2, sm: 2 }
        }}
      >
        <CardMedia
          component="img"
          image={photoUrl}
          alt={altText}
          loading={isFirst ? "eager" : "lazy"}
          fetchPriority={isFirst ? "high" : undefined}
          width="400"
          height="320"
          sx={{ height: { xs: 320, sm: 290 }, borderRadius: 1, objectFit: 'cover' }}
        />
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            px: { xs: 0, sm: 1 }
          }}
        >
          <Box>
            <Typography variant="h6" color="secondary">{classItem.name}</Typography>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              {isLong && !expanded ? `${preview}...` : classItem.description}
            </Typography>
            {isLong && (
              <Typography
                variant="caption"
                sx={{ color: 'gray', mt: 1, cursor: 'pointer', fontStyle: 'italic' }}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Show less' : 'Read more'}
              </Typography>
            )}
          </Box>

        </CardContent>
      </Card>
    </motion.div>
  );
}

export default ClassCard;
